import React from 'react';
import {
  Autocomplete,
  Button,
  Popover,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { NOTE_TEMPLATES } from './noteTemplates.js';
import {
  buildEvidenceOptions,
  templateContextFromSelections,
} from './investigationFeedHelpers.js';

const EMPTY_SELECTED_EVIDENCE = { gm: null, cpq: null, appsheet: null };

const defaultEvidenceTabForTemplate = (templateId) => {
  if (['add_to_gm', 'remove_from_cpq'].includes(templateId)) return 'cpq';
  return 'gm';
};

export const InvestigationTemplatePicker = ({
  activeAuditAction = null,
  anchorEl = null,
  appsheetData = null,
  cpqItems = [],
  gmItems = [],
  onApplyTemplate,
  onClose,
  templateId = '',
}) => {
  const [evidenceTab, setEvidenceTab] = React.useState(() => defaultEvidenceTabForTemplate(templateId));
  const [selectedEvidence, setSelectedEvidence] = React.useState(EMPTY_SELECTED_EVIDENCE);
  const selectedTemplate = NOTE_TEMPLATES.find((template) => template.id === templateId);
  const evidenceOptions = React.useMemo(
    () => buildEvidenceOptions({ gmItems, cpqItems, appsheetData }),
    [appsheetData, cpqItems, gmItems],
  );
  const selectedTemplateHasContext = Boolean(
    selectedEvidence.gm
    || selectedEvidence.cpq
    || selectedEvidence.appsheet
    || activeAuditAction,
  );

  React.useEffect(() => {
    setEvidenceTab(defaultEvidenceTabForTemplate(templateId));
    setSelectedEvidence(EMPTY_SELECTED_EVIDENCE);
  }, [templateId]);

  const applySelectedTemplate = () => {
    if (!selectedTemplate) return;
    onApplyTemplate(selectedTemplate.id, templateContextFromSelections({
      templateId: selectedTemplate.id,
      ...selectedEvidence,
      auditAction: activeAuditAction || null,
    }));
    onClose();
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{ paper: { sx: { p: 1.25, width: 360, maxWidth: 'calc(100vw - 32px)' } } }}
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            {selectedTemplate?.label || 'Note template'}
          </Typography>
          <Button
            size="small"
            variant="contained"
            onClick={applySelectedTemplate}
            disabled={!selectedTemplateHasContext}
          >
            Apply template
          </Button>
        </Stack>
        <Tabs
          value={evidenceTab}
          onChange={(_event, nextTab) => setEvidenceTab(nextTab)}
          variant="fullWidth"
          aria-label="Note evidence source"
        >
          <Tab value="gm" label="GM" />
          <Tab value="cpq" label="CPQ" />
          <Tab value="appsheet" label="AppSheet" />
        </Tabs>
        <Autocomplete
          disablePortal
          size="small"
          options={evidenceOptions[evidenceTab] || []}
          value={selectedEvidence[evidenceTab] || null}
          onChange={(_event, value) => setSelectedEvidence((prev) => ({ ...prev, [evidenceTab]: value }))}
          getOptionLabel={(option) => option?.label || ''}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          renderInput={(params) => (
            <TextField {...params} label={`${evidenceTab === 'gm' ? 'GM' : evidenceTab === 'cpq' ? 'CPQ' : 'AppSheet'} product`} placeholder="Select product evidence" />
          )}
        />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Pick source rows first; applying a template fills product, lane, price, and evidence fields automatically.
        </Typography>
      </Stack>
    </Popover>
  );
};
